import { TEMPLATE_VARIABLES } from '@beta-assur/rules';
import { CHANNELS, LANGUAGES, PRODUCT_FAMILIES, templateCreateSchema } from '@beta-assur/shared';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { z } from 'zod';
import { Field, PageHeader } from '../components/page';
import { ActionError, Empty, QueryView } from '../components/states';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog } from '../components/ui/dialog';
import { Input, Select, Textarea } from '../components/ui/input';
import { Table, TBody, TD, TH, THead, TR } from '../components/ui/table';
import { useAuth } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import { useApiMutation, useApiQuery } from '../lib/query';
import type { Scenario, ScenarioList, TemplateList } from '../lib/types';
import { Tabs } from './portfolio';
import { fieldErrors } from './portfolio-forms';

// Scénarios et modèles (BA-024, spec 08 §4) : listes, bibliothèque par défaut, création.

export const scenariosSearchSchema = z.object({
  tab: z.enum(['scenarios', 'modeles']).optional(),
  channel: z.enum(CHANNELS).optional(),
  language: z.enum(LANGUAGES).optional(),
});
type ScenariosSearch = z.infer<typeof scenariosSearchSchema>;

export const VARIABLES_HINT = TEMPLATE_VARIABLES.map((v) => `{{${v}}}`).join(' ');

const STATUS_TONE = { draft: 'warning', published: 'success', archived: 'neutral' } as const;
const TEMPLATE_TONE = {
  draft: 'warning',
  pending: 'info',
  approved: 'success',
  rejected: 'danger',
} as const;

function NewScenarioDialog() {
  const t = useI18n().t;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [family, setFamily] = useState('');
  const templates = useApiQuery<TemplateList>('/v1/templates', { channel: 'sms' });
  const create = useApiMutation<unknown, Scenario>('POST', '/v1/scenarios', {
    invalidate: ['/v1/scenarios'],
    onSuccess: (created) => {
      setOpen(false);
      void navigate({ to: '/scenarios/$id', params: { id: created.id } });
    },
  });
  const firstSms = templates.data?.items[0]?.id;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!firstSms) return;
    create.mutate({
      name,
      productFamily: family || null,
      steps: [{ kind: 'message', offsetDays: -7, channelPrimary: 'sms', templateId: firstSms }],
    });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
      title={t.scenarios.newScenario}
      trigger={<Button size="sm">{t.scenarios.newScenario}</Button>}
    >
      <form onSubmit={submit} className="flex flex-col gap-3">
        <Field label={t.scenarios.name}>
          <Input value={name} onChange={(e) => setName(e.target.value)} required />
        </Field>
        <Field label={t.scenarios.family}>
          <Select value={family} onChange={(e) => setFamily(e.target.value)}>
            <option value="">{t.scenarios.anyFamily}</option>
            {PRODUCT_FAMILIES.map((f) => (
              <option key={f} value={f}>
                {t.families[f]}
              </option>
            ))}
          </Select>
        </Field>
        <ActionError error={create.error} />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setOpen(false)}>
            {t.common.cancel}
          </Button>
          <Button type="submit" disabled={!name.trim() || !firstSms || create.isPending}>
            {t.common.create}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function NewTemplateDialog() {
  const t = useI18n().t;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', channel: 'sms', language: 'fr', body: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const create = useApiMutation<unknown, { id: string }>('POST', '/v1/templates', {
    invalidate: ['/v1/templates'],
    onSuccess: (created) => {
      setOpen(false);
      void navigate({ to: '/modeles/$id', params: { id: created.id } });
    },
  });
  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  function submit(event: FormEvent) {
    event.preventDefault();
    const parsed = templateCreateSchema.safeParse(form);
    if (!parsed.success) return setErrors(fieldErrors(parsed.error.issues));
    setErrors({});
    create.mutate(parsed.data);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
      title={t.scenarios.newTemplate}
      trigger={<Button size="sm">{t.scenarios.newTemplate}</Button>}
    >
      <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2" noValidate>
        <Field label={t.scenarios.templateName} error={errors.name} className="sm:col-span-2">
          <Input value={form.name} onChange={set('name')} dir="ltr" />
        </Field>
        <Field label={t.scenarios.channel}>
          <Select value={form.channel} onChange={set('channel')}>
            {CHANNELS.map((c) => (
              <option key={c} value={c}>
                {t.channels[c]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t.common.language}>
          <Select value={form.language} onChange={set('language')}>
            {LANGUAGES.map((l) => (
              <option key={l} value={l}>
                {t.languages[l]}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label={t.scenarios.body}
          hint={`${t.scenarios.variables} : ${VARIABLES_HINT}`}
          error={errors.body}
          className="sm:col-span-2"
        >
          <Textarea
            value={form.body}
            onChange={set('body')}
            dir={form.language === 'ar' ? 'rtl' : 'ltr'}
          />
        </Field>
        <div className="sm:col-span-2">
          <ActionError error={create.error} />
        </div>
        <div className="flex justify-end gap-2 sm:col-span-2">
          <Button variant="ghost" onClick={() => setOpen(false)}>
            {t.common.cancel}
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {t.common.create}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function ScenarioTable() {
  const t = useI18n().t;
  const query = useApiQuery<ScenarioList>('/v1/scenarios');
  return (
    <QueryView query={query}>
      {(data) =>
        data.items.length === 0 ? (
          <Empty />
        ) : (
          <Table>
            <THead>
              <tr>
                <TH>{t.scenarios.name}</TH>
                <TH>{t.scenarios.family}</TH>
                <TH>{t.scenarios.status}</TH>
                <TH>{t.scenarios.version}</TH>
              </tr>
            </THead>
            <TBody>
              {data.items.map((s) => (
                <TR key={s.id}>
                  <TD>
                    <Link
                      to="/scenarios/$id"
                      params={{ id: s.id }}
                      className="font-medium text-primary hover:underline"
                    >
                      {s.name}
                    </Link>
                    {s.isDefault ? <Badge className="ms-2">{t.scenarios.isDefault}</Badge> : null}
                  </TD>
                  <TD>{s.productFamily ? t.families[s.productFamily] : t.scenarios.anyFamily}</TD>
                  <TD>
                    <Badge tone={STATUS_TONE[s.status]}>{t.scenarios.statuses[s.status]}</Badge>
                    {s.hasDraft ? (
                      <Badge tone="warning" className="ms-2">
                        {t.scenarios.hasDraft}
                      </Badge>
                    ) : null}
                  </TD>
                  <TD>
                    v{s.version}
                    {s.publishedVersion ? ` (${t.scenarios.published} v${s.publishedVersion})` : ''}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )
      }
    </QueryView>
  );
}

function TemplateTable({ search }: { search: ScenariosSearch }) {
  const t = useI18n().t;
  const navigate = useNavigate();
  const query = useApiQuery<TemplateList>('/v1/templates', {
    channel: search.channel,
    language: search.language,
  });
  const go = (patch: Partial<ScenariosSearch>) =>
    void navigate({ to: '/scenarios', search: { ...search, tab: 'modeles', ...patch } });
  return (
    <>
      <div className="flex flex-wrap gap-2 p-3">
        <Select
          aria-label={t.scenarios.channel}
          className="w-auto"
          value={search.channel ?? ''}
          onChange={(e) =>
            go({ channel: (e.target.value || undefined) as ScenariosSearch['channel'] })
          }
        >
          <option value="">
            {t.scenarios.channel} : {t.common.all}
          </option>
          {CHANNELS.map((c) => (
            <option key={c} value={c}>
              {t.channels[c]}
            </option>
          ))}
        </Select>
        <Select
          aria-label={t.common.language}
          className="w-auto"
          value={search.language ?? ''}
          onChange={(e) =>
            go({ language: (e.target.value || undefined) as ScenariosSearch['language'] })
          }
        >
          <option value="">
            {t.common.language} : {t.common.all}
          </option>
          {LANGUAGES.map((l) => (
            <option key={l} value={l}>
              {t.languages[l]}
            </option>
          ))}
        </Select>
      </div>
      <QueryView query={query}>
        {(data) =>
          data.items.length === 0 ? (
            <Empty />
          ) : (
            <Table>
              <THead>
                <tr>
                  <TH>{t.scenarios.name}</TH>
                  <TH>{t.scenarios.channel}</TH>
                  <TH>{t.common.language}</TH>
                  <TH>{t.scenarios.status}</TH>
                  <TH>{t.scenarios.body}</TH>
                </tr>
              </THead>
              <TBody>
                {data.items.map((tpl) => (
                  <TR key={tpl.id}>
                    <TD>
                      <Link
                        to="/modeles/$id"
                        params={{ id: tpl.id }}
                        className="font-medium text-primary hover:underline"
                      >
                        {tpl.name}
                      </Link>
                    </TD>
                    <TD>{t.channels[tpl.channel]}</TD>
                    <TD>{t.languages[tpl.language]}</TD>
                    <TD>
                      <Badge tone={TEMPLATE_TONE[tpl.status]}>
                        {t.scenarios.templateStatuses[tpl.status]}
                      </Badge>
                    </TD>
                    <TD className="max-w-md truncate" dir={tpl.language === 'ar' ? 'rtl' : 'ltr'}>
                      {tpl.body}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )
        }
      </QueryView>
    </>
  );
}

export function ScenariosPage() {
  const t = useI18n().t;
  const auth = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as ScenariosSearch;
  const tab = search.tab ?? 'scenarios';
  const [installed, setInstalled] = useState<string | null>(null);
  const install = useApiMutation<undefined, { templates: number; scenarios: number }>(
    'POST',
    '/v1/reminders/library',
    {
      invalidate: ['/v1/scenarios', '/v1/templates'],
      onSuccess: (r) =>
        setInstalled(`${t.scenarios.libraryInstalled} : ${r.scenarios} / ${r.templates}`),
    },
  );
  const admin = auth.can('administer');

  return (
    <>
      <PageHeader
        title={t.scenarios.title}
        actions={
          admin ? (
            <>
              <Button
                size="sm"
                variant="outline"
                disabled={install.isPending}
                onClick={() => install.mutate(undefined)}
              >
                {t.scenarios.installLibrary}
              </Button>
              {tab === 'scenarios' ? <NewScenarioDialog /> : <NewTemplateDialog />}
            </>
          ) : null
        }
      />
      {installed ? (
        <p role="status" className="mb-3 text-sm text-success">
          {installed}
        </p>
      ) : null}
      <ActionError error={install.error} />
      <Tabs
        label={t.scenarios.title}
        active={tab}
        onChange={(key) => void navigate({ to: '/scenarios', search: { tab: key } })}
        tabs={[
          { key: 'scenarios', label: t.scenarios.scenarios },
          { key: 'modeles', label: t.scenarios.templates },
        ]}
      />
      <Card>{tab === 'scenarios' ? <ScenarioTable /> : <TemplateTable search={search} />}</Card>
    </>
  );
}
